import { Formik } from 'formik'
import { isEmpty, keys, isArray } from 'lodash'
import { ReactElement, useState } from 'react'
import { defineMessages, useIntl, FormattedMessage } from 'react-intl'

import { SelectTags } from 'components/v2/SelectTags'
import { Tag } from 'components/v2/Tag'
import { Select } from 'components/v2/Select'
import { DOW } from 'features/FareFamilies/components/Dow/constants'
import { dowMessages } from 'features/FareFamilies/components/Dow/messages'
import { STATUS } from 'features/Services/components/Status/constants'
import { statusMessages } from 'features/Services/components/Status/messages'
import { CONFIRMATION_MODALITIES } from 'features/Services/components/ConfirmationModalities/constants'
import { confirmationModalitiesMessages } from 'features/Services/components/ConfirmationModalities/messages'
import { RouteVehicleAutocomplete } from 'features/Routes/components/RouteVehicleAutocomplete'
import { safeFormatMessage } from 'utils/intl'
import { filterEmptyValue } from 'utils/object'
import {
  FilterHeader,
  FilterToggle,
  FilterWrapper,
  FiltersContainer,
  FiltersSubmit,
  FiltersWrapper,
  TagsWrapper
} from 'components/Filters'

const STATUS_LIST = [STATUS.ACTIVE, STATUS.INACTIVE]

const MODALITIES_LIST = [
  CONFIRMATION_MODALITIES.PAID,
  CONFIRMATION_MODALITIES.RESERVED,
  CONFIRMATION_MODALITIES.FACEBOOK
]

const DOW_LIST = [DOW.MONDAY, DOW.TUESDAY, DOW.WEDNESDAY, DOW.THURSDAY, DOW.FRIDAY, DOW.SATURDAY, DOW.SUNDAY]

const messages = defineMessages({
  selectStatus: {
    id: 'services.filters.selectStatus',
    defaultMessage: 'Select status'
  },
  selectVehicle: {
    id: 'services.filters.selectVehicle',
    defaultMessage: 'Select vehicle'
  },
  selectModality: {
    id: 'services.filters.selectModality',
    defaultMessage: 'Select modality'
  },
  selectDow: {
    id: 'services.filters.selectDow',
    defaultMessage: 'Select day of week'
  }
})

const getFilteredModalities = (values: TSFixMe): TSFixMe => {
  return MODALITIES_LIST.filter((modality): TSFixMe => {
    return values.indexOf(modality) === -1
  })
}

const getFilteredDow = (values: TSFixMe): TSFixMe => {
  return DOW_LIST.filter((day): TSFixMe => {
    return values.indexOf(day) === -1
  })
}

export const ServicesFilters = ({ filters = {}, routeId, ...props }: TSFixMe): TSFixMe => {
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): TSFixMe => {
    showFilters(!isShowingFilters)
  }

  const filterServices = (values: TSFixMe): TSFixMe => {
    const filters = {
      status: values.status,
      vehicle: {
        id: values.vehicle.id,
        name: values.vehicle.name
      },
      modalities: values.modalities,
      dow: values.dow
    }

    props.handleChange(filterEmptyValue(filters))
  }

  const initialValues = {
    status: filters.status || '',
    vehicle: filters.vehicle || { id: '', name: '' },
    modalities: filters.modalities || [],
    dow: filters.dow || []
  }

  return (
    <Formik initialValues={initialValues} onSubmit={filterServices} enableReinitialize>
      {(props): ReactElement => {
        const { handleSubmit, setFieldValue, values, handleChange } = props

        const handleFilterDelete = (id: keyof typeof initialValues): void => {
          if (isArray(values[id])) {
            setFieldValue(id, [])
            filterServices({
              ...values,
              [id]: []
            })
          } else {
            setFieldValue(id, '')
            filterServices({
              ...values,
              [id]: ''
            })
          }
        }

        const handleSelectChange = (event: TSFixMe): TSFixMe => {
          const {
            target: { id, value }
          } = event

          handleChange(event)

          filterServices({
            ...values,
            [id]: value
          })
        }

        const handleModalitiesChange = (modalities: TSFixMe): TSFixMe => {
          setFieldValue('modalities', modalities)

          filterServices({
            ...values,
            modalities
          })
        }

        const handleDowChange = (dow: TSFixMe): TSFixMe => {
          setFieldValue('dow', dow)

          filterServices({
            ...values,
            dow
          })
        }

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <div>
                    <FilterToggle onClick={toggleFilters} />
                  </div>
                  <TagsWrapper>
                    {keys(filters).map((filterKey): TSFixMe => {
                      let label, value
                      const filterValue = filters[filterKey]

                      const hasValue = !isEmpty(filterValue)

                      if (!hasValue) return null

                      switch (filterKey) {
                        case 'status':
                          label = <FormattedMessage id="dictionary.status" defaultMessage="Status" />
                          // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
                          value = intl.formatMessage(statusMessages[filterValue])
                          break
                        case 'vehicle':
                          if (!filterValue.id) return null

                          label = <FormattedMessage id="dictionary.vehicle" defaultMessage="Vehicle" />
                          value = filterValue.name
                          break
                        case 'modalities':
                          label = <FormattedMessage id="dictionary.modalities" defaultMessage="Modalities" />
                          value = filterValue
                            .map((modality: TSFixMe): TSFixMe => {
                              return (
                                // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
                                confirmationModalitiesMessages[modality] &&
                                // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
                                intl.formatMessage(confirmationModalitiesMessages[modality])
                              )
                            })
                            .join(', ')
                          break
                        case 'dow':
                          label = <FormattedMessage id="dictionary.dow" defaultMessage="Dow" />
                          value = filterValue
                            .map((day: TSFixMe): TSFixMe => {
                              return dowMessages[day] && intl.formatMessage(dowMessages[day])
                            })
                            .join(', ')
                          break
                      }

                      return (
                        <Tag
                          key={filterKey}
                          onDelete={handleFilterDelete.bind(this, filterKey as keyof typeof initialValues)}
                        >
                          {label}: {value}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <Select
                    id="status"
                    value={values.status}
                    onChange={handleSelectChange}
                    label={<FormattedMessage id="dictionary.status" defaultMessage="Status" />}
                  >
                    <option value="">{intl.formatMessage(messages.selectStatus)}</option>

                    {STATUS_LIST.map((status): TSFixMe => {
                      return (
                        <option value={status} key={status}>
                          {/* @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message*/}
                          {statusMessages[status] && intl.formatMessage(statusMessages[status])}
                        </option>
                      )
                    })}
                  </Select>
                  <RouteVehicleAutocomplete
                    placeholder={intl.formatMessage(messages.selectVehicle)}
                    value={values.vehicle.name}
                    label={<FormattedMessage id="dictionary.vehicle" defaultMessage="Vehicle" />}
                    onChange={(vehicle: TSFixMe): TSFixMe => {
                      setFieldValue('vehicle', vehicle)

                      filterServices({
                        ...values,
                        vehicle: {
                          id: vehicle.id,
                          name: vehicle.name
                        }
                      })
                    }}
                    routeId={routeId}
                  />
                  <SelectTags
                    id="modalities"
                    placeholder={intl.formatMessage(messages.selectModality)}
                    values={values.modalities}
                    items={getFilteredModalities(values.modalities)}
                    getTagLabel={(modality): TSFixMe => {
                      return safeFormatMessage({ intl, key: modality, messages: confirmationModalitiesMessages })
                    }}
                    onChange={handleModalitiesChange}
                    label={<FormattedMessage id="dictionary.modalities" defaultMessage="Modalities" />}
                  />
                  <SelectTags
                    id="dow"
                    placeholder={intl.formatMessage(messages.selectDow)}
                    values={values.dow}
                    items={getFilteredDow(values.dow)}
                    getTagLabel={(day): TSFixMe => {
                      return intl.formatMessage(dowMessages[day])
                    }}
                    onChange={handleDowChange}
                    label={<FormattedMessage id="dictionary.dow" defaultMessage="Dow" />}
                  />
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <FiltersSubmit />
          </form>
        )
      }}
    </Formik>
  )
}
